(function () {
  'use strict';

  var _s = window.__ddCartShared = window.__ddCartShared || {};
  _s.granularFooterPending = false;
  /* Treat the SSR-rendered drawer as fresh on page load. The Liquid
     render of cart-drawer.liquid IS a section render — it's the same
     HTML the /cart?sections=cart-drawer endpoint would return for
     this state. There's no value in re-fetching it on the first cart
     icon click. The 60s freshness window means subsequent clicks
     within a minute also skip the fetch. After that, the fetch +
     painter run again as a cross-tab safety net.

     Use `||` so we don't overwrite a real timestamp if the script is
     re-evaluated after a section swap (which happens when a section
     containing this asset link gets re-included). The initial value
     wins; subsequent painter runs update it via the legitimate path
     in `patchDrawerFromSection`. */
  if (!_s.sectionFooterFreshTs) _s.sectionFooterFreshTs = Date.now();

  var SHIMMER_MS = 320;
  var CUSTOM_INFO_EXIT_MS = 240;
  var XSELL_COLLAPSE_MS = 400;
  var _drawerItemsSwapToken = 0;
  var _sectionReplaceInProgress = false;
  var _sectionReplaceQueue = [];

  function metrics() { return window.__ddCartMetrics || {}; }
  function facade() { return window.ddCartHelpers || {}; }
  function debugLog(name, data) { var f = facade(); if (f.debugLog) f.debugLog(name, data); }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function withLocalShimmer(el, fn) {
    if (!el) { if (fn) fn(); return; }
    if (prefersReducedMotion()) { fn(); return; }
    el.classList.add('dd-mutate-shimmer');
    try { fn(); } catch (err) { console.error('[cart-paint] withLocalShimmer', err); }
    window.setTimeout(function () { el.classList.remove('dd-mutate-shimmer'); }, SHIMMER_MS);
  }

  function rehydrateBogosVisibility() {
    if (typeof window._ddBogosInlineGiftRender === 'function') window._ddBogosInlineGiftRender();
  }

  /* ----------------------------------------------------------------
   * Cart-page footer capture / restore around full-section swap
   * --------------------------------------------------------------
   * `replaceCartSections` does a wholesale `target.innerHTML = html`
   * for `#main-cart-footer .js-contents` (no granular patcher
   * exists for the cart-page footer the way `patchDrawerFromSection`
   * does for the drawer). That swap fires on every cart mutation —
   * including the ones the shopper just initiated (apply discount,
   * save note, qty change) — and rebuilds the entire footer subtree
   * from fresh SSR. Anything the shopper-side JS just wrote into
   * the footer DOM is lost:
   *
   *   - Open/closed state of the discount + note accordions
   *     (`is--open` class, `aria-expanded`).
   *   - Inline `.dd-discount__feedback` success / warn messages
   *     (set by `dd-discount-code.js` showFeedback).
   *   - The `[data-dd-cart-toast]` host's `is--visible` + content
   *     (set by `cart-toast.js` showInlineToast).
   *   - In-progress input text the shopper hasn't submitted yet
   *     (discount input, note input).
   *   - The note input's `data-dd-initial-note` baseline used by
   *     the unsaved-note checkout guard.
   *
   * Capture before the swap, restore right after. Anything we don't
   * recognize falls through to the SSR default (the right answer
   * for any DOM the section template owns).
   * --------------------------------------------------------------*/
  function captureAccordionState(root, selector) {
    var acc = root.querySelector(selector);
    if (!acc) return null;
    var toggle = acc.querySelector('[data-dd-accordion-toggle]');
    return {
      isOpen: acc.classList.contains('is--open'),
      ariaExpanded: toggle ? toggle.getAttribute('aria-expanded') : null
    };
  }

  function restoreAccordionState(root, selector, state) {
    if (!state) return;
    var acc = root.querySelector(selector);
    if (!acc) return;
    if (state.isOpen) acc.classList.add('is--open');
    var toggle = acc.querySelector('[data-dd-accordion-toggle]');
    if (toggle && state.ariaExpanded != null) toggle.setAttribute('aria-expanded', state.ariaExpanded);
  }

  function captureCartPageFooterEphemera(target) {
    if (!target) return null;
    var snap = {};

    snap.discountAccordion = captureAccordionState(target, '[data-dd-discount-accordion]');
    snap.noteAccordion     = captureAccordionState(target, '[data-dd-note-accordion]');

    var feedback = target.querySelector('.dd-discount__feedback');
    if (feedback && feedback.classList.contains('is--visible')) {
      snap.feedback = {
        html:      feedback.innerHTML,
        className: feedback.className
      };
    }

    var toast = target.querySelector('[data-dd-cart-toast]');
    if (toast && toast.classList.contains('is--visible')) {
      var msgEl  = toast.querySelector('[data-dd-cart-toast-msg]');
      var iconEl = toast.querySelector('[data-dd-cart-toast-icon]');
      snap.toast = {
        className: toast.className,
        msg:       msgEl  ? msgEl.textContent : '',
        iconHtml:  iconEl ? iconEl.innerHTML  : ''
      };
    }

    var discountInput = target.querySelector('[data-dd-discount-input]');
    if (discountInput && discountInput.value) snap.discountInputValue = discountInput.value;

    var noteInput = target.querySelector('[data-dd-note-input]');
    if (noteInput) {
      snap.noteInputValue       = noteInput.value || '';
      snap.noteInputInitialNote = noteInput.getAttribute('data-dd-initial-note') || '';
    }

    return snap;
  }

  function restoreCartPageFooterEphemera(target, snap) {
    if (!target || !snap) return;

    restoreAccordionState(target, '[data-dd-discount-accordion]', snap.discountAccordion);
    restoreAccordionState(target, '[data-dd-note-accordion]',     snap.noteAccordion);

    if (snap.feedback) {
      var feedback = target.querySelector('.dd-discount__feedback');
      if (feedback) {
        feedback.innerHTML = snap.feedback.html;
        feedback.className = snap.feedback.className;
      }
    }

    if (snap.toast) {
      var toast = target.querySelector('[data-dd-cart-toast]');
      if (toast) {
        toast.className = snap.toast.className;
        var msgEl  = toast.querySelector('[data-dd-cart-toast-msg]');
        var iconEl = toast.querySelector('[data-dd-cart-toast-icon]');
        if (msgEl)  msgEl.textContent  = snap.toast.msg;
        if (iconEl) iconEl.innerHTML   = snap.toast.iconHtml;
      }
    }

    /* Only restore in-progress input if the shopper has unsubmitted text;
       skip when the freshly-rendered SSR value already matches (avoids
       clobbering the canonical "applied" state with stale input text). */
    if (snap.discountInputValue) {
      var discountInput = target.querySelector('[data-dd-discount-input]');
      if (discountInput && !discountInput.value) discountInput.value = snap.discountInputValue;
    }

    if (snap.noteInputValue || snap.noteInputInitialNote) {
      var noteInput = target.querySelector('[data-dd-note-input]');
      if (noteInput) {
        if (snap.noteInputValue && noteInput.value !== snap.noteInputValue) {
          /* Unsaved edit — keep the shopper's draft text */
          noteInput.value = snap.noteInputValue;
        }
        /* The SSR baseline is the just-saved note (canonical). Keep it
           if the shopper has no unsaved draft; otherwise restore the
           captured baseline so the unsaved-note guard still trips. */
        if (snap.noteInputValue && snap.noteInputValue !== snap.noteInputInitialNote) {
          noteInput.setAttribute('data-dd-initial-note', snap.noteInputInitialNote);
        }
      }
    }
  }

  /* ----------------------------------------------------------------
   * dd-cart:items-swapped
   * --------------------------------------------------------------
   * Stable post-swap signal for any surface (cross-sell hydration,
   * variant-map augmentation, third-party widgets) that needs to act
   * on the FRESH cart DOM after a section replacement.
   *
   * Why this exists: cart-queue.js publishes PUB_SUB_EVENTS.cartUpdate
   * immediately after replaceCartSections() returns, but the actual
   * innerHTML swap inside patchDrawerFromSection can be deferred up
   * to XSELL_COLLAPSE_MS / CUSTOM_INFO_EXIT_MS to play exit
   * animations. cartUpdate subscribers therefore run against STALE
   * pre-swap DOM and any post-swap mutations they make are lost when
   * the deferred innerHTML assignment overwrites them. This event is
   * dispatched ONLY after the actual DOM swap, so subscribers get a
   * truthful "your DOM is fresh now" signal regardless of timing.
   *
   * Scope values:
   *   - 'drawer-items'  → CartDrawer-Items innerHTML replaced
   *   - 'drawer-root'   → entire #CartDrawer innerHTML replaced
   *   - 'cart-page'     → main-cart-items / main-cart-footer js-contents replaced
   * --------------------------------------------------------------*/
  function emitItemsSwapped(scope) {
    try {
      document.dispatchEvent(new CustomEvent('dd-cart:items-swapped', {
        detail: { scope: scope || 'unknown', ts: Date.now() }
      }));
    } catch (e) { /* CustomEvent unsupported — silently no-op */ }
  }

  function replaceDrawerInnerHTML(drawerInner, freshHTML) {
    if (!drawerInner || !freshHTML) return;
    _s.granularFooterPending = false;
    drawerInner.innerHTML = freshHTML;
    rehydrateBogosVisibility();
    emitItemsSwapped('drawer-inner');
  }

  function replaceCartDrawerRootHTML(cartDrawerRoot, freshInnerHTML) {
    if (!cartDrawerRoot || !freshInnerHTML) return;
    _s.granularFooterPending = false;
    cartDrawerRoot.innerHTML = freshInnerHTML;
    rehydrateBogosVisibility();
    emitItemsSwapped('drawer-root');
  }

  function parseCartDrawerRootInnerHtml(rawDrawerSectionHtml) {
    if (!rawDrawerSectionHtml) return '';
    var doc = new DOMParser().parseFromString(rawDrawerSectionHtml, 'text/html');
    var root = doc.querySelector('cart-drawer #CartDrawer') || doc.querySelector('#CartDrawer');
    return root ? root.innerHTML : '';
  }

  function patchDrawerFromSection(rawDrawerSectionHtml) {
    var m = metrics();
    if (!rawDrawerSectionHtml) { m.drawerPatchFail = (m.drawerPatchFail || 0) + 1; return false; }

    var liveRoot = document.getElementById('CartDrawer');
    if (!liveRoot) { m.drawerPatchFail = (m.drawerPatchFail || 0) + 1; return false; }

    var doc = new DOMParser().parseFromString(rawDrawerSectionHtml, 'text/html');
    var nextRoot = doc.querySelector('#CartDrawer');
    if (!nextRoot) { m.drawerPatchFail = (m.drawerPatchFail || 0) + 1; return false; }

    var liveItemsShell = liveRoot.querySelector('#CartDrawer-Items');
    var nextItemsShell = nextRoot.querySelector('#CartDrawer-Items');
    if (!liveItemsShell || !nextItemsShell) { m.drawerPatchFail = (m.drawerPatchFail || 0) + 1; return false; }

    _sectionReplaceInProgress = true;
    var swapToken = ++_drawerItemsSwapToken;
    var delayMs = 0;

    var liveCustomCard = liveItemsShell.querySelector('.dd-custom-card');
    var nextHasCustomCard = !!nextItemsShell.querySelector('.dd-custom-card');
    if (liveCustomCard && !nextHasCustomCard) {
      liveCustomCard.classList.add('is--exit');
      delayMs = Math.max(delayMs, CUSTOM_INFO_EXIT_MS);
    }

    var liveXsellAdded = liveItemsShell.querySelector('.ktip-xsell.is--added');
    if (liveXsellAdded) delayMs = Math.max(delayMs, XSELL_COLLAPSE_MS);

    var nextItemsHtml = nextItemsShell.innerHTML;

    function finishItemsSwap() {
      if (_drawerItemsSwapToken !== swapToken) { _sectionReplaceInProgress = false; return; }
      if (liveItemsShell.isConnected) {
        /* Equality gate: skip the wholesale innerHTML swap when the
           server returned the same markup we already have. This is
           the dominant case on cold drawer-opens (no real mutation
           between opens) — without the gate every reopen flashes the
           items list as the browser re-parses, re-renders, and
           re-runs IntersectionObservers / lazy-load handlers on
           identical markup. With the gate, opens with no change are
           true no-ops. */
        if (liveItemsShell.innerHTML !== nextItemsHtml) {
          liveItemsShell.innerHTML = nextItemsHtml;
          rehydrateBogosVisibility();
          emitItemsSwapped('drawer-items');
        }
      }
      _sectionReplaceInProgress = false;
      if (_sectionReplaceQueue.length > 0) {
        var nextHtml = _sectionReplaceQueue.pop();
        _sectionReplaceQueue = [];
        window.setTimeout(function () { patchDrawerFromSection(nextHtml); }, 0);
      }
    }

    if (delayMs > 0) window.setTimeout(finishItemsSwap, delayMs);
    else finishItemsSwap();

    var liveFooter = liveRoot.querySelector('.dd-drawer-footer');
    var nextFooter = nextRoot.querySelector('.dd-drawer-footer');
    if (liveFooter && nextFooter) {
      var liveSummary = liveFooter.querySelector('[data-dd-total-discount]');
      var nextSummary = nextFooter.querySelector('[data-dd-total-discount]');
      if (liveSummary && nextSummary) {
        liveSummary.setAttribute('data-dd-total-discount', String(nextSummary.getAttribute('data-dd-total-discount') || '0'));
      }

      var liveDisc = liveFooter.querySelector('[data-dd-cart-discounts]');
      var nextDisc = nextFooter.querySelector('[data-dd-cart-discounts]');
      if (liveDisc && nextDisc) {
        var nextResolvedName = nextDisc.getAttribute('data-dd-resolved-discount-name') || '';
        liveDisc.setAttribute('data-dd-resolved-discount-name', nextResolvedName);
        /* Equality gate matches the subtotal-block / checkout-block
           gates a few lines below. Without it, every cold drawer
           open shimmers the discount strip even when nothing changed
           — and any client-rendered chips (e.g. SF-API gift cards
           injected by `dd-storefront-cart.js`) get wiped only to be
           re-injected ~50ms later, producing a visible flicker. */
        if (liveDisc.innerHTML !== nextDisc.innerHTML) {
          withLocalShimmer(liveDisc, function () { liveDisc.innerHTML = nextDisc.innerHTML; });
          if (window.ddDiscountChips && typeof window.ddDiscountChips.render === 'function') window.ddDiscountChips.render();
        }
      }

      var liveSavings = liveFooter.querySelector('[data-dd-savings]');
      var nextSavings = nextFooter.querySelector('[data-dd-savings]');
      if (liveSavings && nextSavings) {
        /* Skip the shimmer when the savings number + display state
           are unchanged. We compare the actual stable inputs (style
           display + money textContent) rather than full innerHTML
           because innerHTML can carry whitespace differences between
           SSR renders that wouldn't actually change the visible
           output. */
        var liveSavingsMoney = liveSavings.querySelector('[data-dd-savings-money]');
        var nextSavingsMoney = nextSavings.querySelector('[data-dd-savings-money]');
        var savingsDisplayChanged = liveSavings.style.display !== nextSavings.style.display;
        var savingsMoneyChanged = liveSavingsMoney && nextSavingsMoney
          && liveSavingsMoney.textContent !== nextSavingsMoney.textContent;
        if (savingsDisplayChanged || savingsMoneyChanged) {
          withLocalShimmer(liveSavings, function () {
            liveSavings.style.display = nextSavings.style.display;
            if (liveSavingsMoney && nextSavingsMoney) liveSavingsMoney.textContent = nextSavingsMoney.textContent;
          });
        }
      }

      var liveSubtotalBlock = liveFooter.querySelector('[data-dd-subtotal-block]');
      var nextSubtotalBlock = nextFooter.querySelector('[data-dd-subtotal-block]');
      if (liveSubtotalBlock && nextSubtotalBlock) {
        if (liveSubtotalBlock.innerHTML !== nextSubtotalBlock.innerHTML) {
          withLocalShimmer(liveSubtotalBlock, function () {
            liveSubtotalBlock.innerHTML = nextSubtotalBlock.innerHTML;
          });
        }
      } else {
        var liveSubtotal = liveFooter.querySelector('[data-dd-subtotal-money]');
        var nextSubtotal = nextFooter.querySelector('[data-dd-subtotal-money]');
        if (liveSubtotal && nextSubtotal && liveSubtotal.textContent !== nextSubtotal.textContent) {
          var subtotalHost = liveSubtotal.closest('[data-dd-subtotal]') || liveSubtotal.parentElement;
          withLocalShimmer(subtotalHost, function () { liveSubtotal.textContent = nextSubtotal.textContent; });
        }
      }

      var liveCheckoutBlock = liveFooter.querySelector('[data-dd-checkout-cta-block]');
      var nextCheckoutBlock = nextFooter.querySelector('[data-dd-checkout-cta-block]');
      if (liveCheckoutBlock && nextCheckoutBlock) {
        if (liveCheckoutBlock.innerHTML !== nextCheckoutBlock.innerHTML) {
          withLocalShimmer(liveCheckoutBlock, function () {
            liveCheckoutBlock.innerHTML = nextCheckoutBlock.innerHTML;
          });
        }
      } else {
        var liveCheckout = liveFooter.querySelector('[data-dd-checkout]') || liveFooter.querySelector('#CartDrawer-Checkout');
        var nextCheckout = nextFooter.querySelector('[data-dd-checkout]') || nextFooter.querySelector('#CartDrawer-Checkout');
        if (liveCheckout && nextCheckout) {
          if (nextCheckout.hasAttribute('disabled')) liveCheckout.setAttribute('disabled', '');
          else liveCheckout.removeAttribute('disabled');
        }
      }
    }

    var liveEmpty = liveRoot.querySelector('[data-dd-cart-empty]');
    var nextEmpty = nextRoot.querySelector('[data-dd-cart-empty]');
    if (liveEmpty && nextEmpty) liveEmpty.style.display = nextEmpty.style.display;

    var liveContent = liveRoot.querySelector('[data-dd-cart-content]');
    var nextContent = nextRoot.querySelector('[data-dd-cart-content]');
    if (liveContent && nextContent) liveContent.style.display = nextContent.style.display;

    var liveDrawerHost = document.querySelector('cart-drawer');
    var nextDrawerHost = doc.querySelector('cart-drawer');
    if (liveDrawerHost && nextDrawerHost) {
      if (nextDrawerHost.classList.contains('is-empty')) liveDrawerHost.classList.add('is-empty');
      else liveDrawerHost.classList.remove('is-empty');
    }

    m.drawerPatchSuccess = (m.drawerPatchSuccess || 0) + 1;
    _s.sectionFooterFreshTs = Date.now();
    return true;
  }

  function applyFooterFromCart(drawer, cart) {
    if (!drawer || !cart) return;
    updateDrawerFooterMeta(drawer, cart);
  }

  function consumeGranularFooterPending() {
    var v = _s.granularFooterPending;
    _s.granularFooterPending = false;
    return v;
  }

  function normalizeSectionList(sections) {
    if (!sections) return [];
    if (Array.isArray(sections)) return sections.filter(Boolean);
    if (typeof sections === 'string') {
      return sections.split(',').map(function (s) { return String(s || '').trim(); }).filter(Boolean);
    }
    return [];
  }

  function hasAllRequestedSections(payload, ids) {
    if (!payload || typeof payload !== 'object') return false;
    for (var i = 0; i < ids.length; i++) {
      if (typeof payload[ids[i]] !== 'string' || !payload[ids[i]]) return false;
    }
    return true;
  }

  function fetchSectionsBySectionId(ids) {
    var unique = {};
    var list = [];
    for (var i = 0; i < ids.length; i++) {
      var id = String(ids[i] || '').trim();
      if (!id || unique[id]) continue;
      unique[id] = true;
      list.push(id);
    }
    if (!list.length) return Promise.resolve(null);

    var tasks = list.map(function (id) {
      var basePath = id === 'cart-drawer' || id === 'cart-icon-bubble' ? '/' : '/cart';
      var url = basePath + '?section_id=' + encodeURIComponent(id);
      var f = facade();
      if (f.recordRequest) f.recordRequest(url, 'sections-fallback-section-id', 'queue');
      return fetch(url, { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('Section fetch failed (' + r.status + '): ' + id);
          return r.text();
        })
        .then(function (html) { return { id: id, html: html || '' }; });
    });

    return Promise.all(tasks).then(function (rows) {
      var out = {};
      for (var i = 0; i < rows.length; i++) {
        if (rows[i] && rows[i].html) out[rows[i].id] = rows[i].html;
      }
      return Object.keys(out).length ? out : null;
    });
  }

  function fetchSectionsFallback(sectionIds) {
    var ids = normalizeSectionList(sectionIds);
    if (!ids.length) return Promise.resolve(null);
    var base = '/cart';
    var url = base + '?sections=' + encodeURIComponent(ids.join(','));
    var f = facade();
    if (f.recordRequest) f.recordRequest(url, 'sections-fallback', 'queue');
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('Sections fallback failed (' + r.status + ')');
        return r.json();
      })
      .then(function (payload) {
        if (payload && typeof payload === 'object' && hasAllRequestedSections(payload, ids)) return payload;
        return fetchSectionsBySectionId(ids);
      })
      .catch(function () { return fetchSectionsBySectionId(ids); });
  }

  function parseSectionHTML(html, selector) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var el = doc.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  function captureCartScrollSnapshot() {
    var main = document.getElementById('main-cart-items');
    var mainContents = main ? main.querySelector('.js-contents') : null;
    var drawerList = document.querySelector('[data-dd-cart-items]');
    return {
      winY: window.scrollY || window.pageYOffset || 0,
      drawerScroll: drawerList ? drawerList.scrollTop : 0,
      mainScroll: mainContents ? mainContents.scrollTop : 0
    };
  }

  function restoreCartScrollSnapshot(snap) {
    if (!snap) return;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        window.scrollTo(0, snap.winY || 0);
        var drawerList = document.querySelector('[data-dd-cart-items]');
        if (drawerList && typeof snap.drawerScroll === 'number') drawerList.scrollTop = snap.drawerScroll;
        var main = document.getElementById('main-cart-items');
        var mainContents = main ? main.querySelector('.js-contents') : null;
        if (mainContents && typeof snap.mainScroll === 'number') mainContents.scrollTop = snap.mainScroll;
      });
    });
  }

  var DISCOUNT_ICON_SVG =
    '<svg viewBox="0 0 448 512" width="12" height="12" aria-hidden="true"><path d="M48 32H197.5C214.5 32 230.7 38.74 242.7 50.75L418.7 226.7C443.7 251.7 443.7 292.3 418.7 317.3L285.3 450.7C260.3 475.7 219.7 475.7 194.7 450.7L18.75 274.7C6.743 262.7 0 246.5 0 229.5V80C0 53.49 21.49 32 48 32zM112 176C129.7 176 144 161.7 144 144C144 126.3 129.7 112 112 112C94.33 112 80 126.3 80 144C80 161.7 94.33 176 112 176z" fill="currentColor"/></svg>';

  function lineDiscountAllocations(item) {
    if (!item) return [];
    var d = item.discount_allocations;
    if (Array.isArray(d) && d.length) return d;
    return Array.isArray(item.line_level_discount_allocations) ? item.line_level_discount_allocations : [];
  }

  function sumDiscountAllocationCents(allocations) {
    var s = 0;
    for (var i = 0; i < allocations.length; i++) {
      var n = Number((allocations[i] && allocations[i].amount) || 0);
      if (isFinite(n)) s += n;
    }
    return s;
  }

  function renderLinePriceRowInner(item) {
    var f_ = facade();
    var formatMoney = f_.formatMoney || function (c) { var f = (c / 100).toFixed(2); return '$' + (f.slice(-3) === '.00' ? f.slice(0, -3) : f); };
    var escHtml = f_.escHtml || function (s) { return String(s || ''); };
    var isProtectionLine = f_.isProtectionLine || function () { return false; };

    var o = item.original_line_price || 0;
    var fi = item.final_line_price != null ? item.final_line_price : item.line_price || 0;
    if (isProtectionLine(item)) {
      return '<span class="dd-cart-item__price">' + formatMoney(o || fi) + '</span>';
    }
    var allocs = lineDiscountAllocations(item);
    var allocSum = sumDiscountAllocationCents(allocs);
    var compareAt = item && item.variant && typeof item.variant.compare_at_price === 'number'
      ? item.variant.compare_at_price : 0;
    var finalUnit = item.final_price != null ? item.final_price : item.price || 0;
    if (o <= fi && compareAt > finalUnit) o = compareAt * (item.quantity || 1);
    if (o <= fi && allocSum > 0) o = fi + allocSum;
    var parts = [];
    if (o > fi) {
      parts.push('<span class="dd-cart-item__price dd-cart-item__price--original">' + formatMoney(o) + '</span>');
      parts.push('<span class="dd-cart-item__price dd-cart-item__price--sale">' + formatMoney(fi) + '</span>');
    } else {
      parts.push('<span class="dd-cart-item__price">' + formatMoney(fi) + '</span>');
    }
    for (var i = 0; i < allocs.length; i++) {
      var a = allocs[i];
      var title = a.discount_application && a.discount_application.title ? a.discount_application.title : '';
      parts.push(
        '<span class="dd-cart-item__discount-badge">' + DISCOUNT_ICON_SVG + escHtml(title) +
        ' (-' + formatMoney(a.amount || 0) + ')</span>'
      );
    }
    return parts.join('');
  }

  function cartSavingsCents(cart) {
    if (cart && typeof cart.total_discount === 'number') return Math.max(0, Number(cart.total_discount) || 0);
    return 0;
  }

  function getAutoDiscountTitle(cart) {
    var allApps = cart.discount_applications || cart.cart_level_discount_applications || [];
    for (var i = 0; i < allApps.length; i++) {
      var app = allApps[i];
      if (app && (app.type === 'automatic' || app.type === 'script' || app.type === 'manual') && app.title) return app.title;
    }
    try {
      var custId = document.documentElement.getAttribute('data-glass-customer-id') || '0';
      var cached = sessionStorage.getItem('dd_regios_name_' + custId);
      if (cached) return cached;
    } catch (e) {}
    return null;
  }

  function renderCartLevelDiscountsHtml(cart) {
    var f_ = facade();
    var formatMoney = f_.formatMoney || function (c) { var f = (c / 100).toFixed(2); return '$' + (f.slice(-3) === '.00' ? f.slice(0, -3) : f); };
    var escHtml = f_.escHtml || function (s) { return String(s || ''); };
    var escAttr = f_.escAttr || function (s) { return String(s || ''); };

    var allApps = cart.discount_applications || cart.cart_level_discount_applications || [];
    var apps = [];
    for (var i = 0; i < allApps.length; i++) {
      if (allApps[i] && allApps[i].type !== 'discount_code') apps.push(allApps[i]);
    }
    if (!apps.length) {
      var fallbackSavings = cartSavingsCents(cart);
      if (fallbackSavings > 0) {
        var autoTitle = getAutoDiscountTitle(cart) || 'Automatic account discount';
        return '<div class="dd-cart-discount" data-dd-discount-badge data-discount-code="' + escAttr(autoTitle) +
          '" data-discount-type="automatic_discount">' + DISCOUNT_ICON_SVG +
          '<span data-dd-auto-discount-label>' + escHtml(autoTitle) + ' (-' + formatMoney(fallbackSavings) + ')</span></div>';
      }
      return '';
    }
    var out = [];
    for (var j = 0; j < apps.length; j++) {
      var d = apps[j];
      var title = d.title || '';
      var type = d.type || '';
      var amt = d.total_allocated_amount || 0;
      out.push(
        '<div class="dd-cart-discount" data-dd-discount-badge data-discount-code="' + escAttr(title) +
        '" data-discount-type="' + escAttr(type) + '">' + DISCOUNT_ICON_SVG +
        '<span data-dd-auto-discount-label>' + escHtml(title) + ' (-' + formatMoney(amt) + ')</span></div>'
      );
    }
    return out.join('');
  }

  function renderCodeChipsHtml(cart) {
    var f_ = facade();
    var escHtml = f_.escHtml || function (s) { return String(s || ''); };
    var escAttr = f_.escAttr || function (s) { return String(s || ''); };

    var allApps = cart.discount_applications || cart.cart_level_discount_applications || [];
    var out = [];
    for (var i = 0; i < allApps.length; i++) {
      var d = allApps[i];
      if (!d || (d.type || '') !== 'discount_code') continue;
      var title = d.title || '';
      out.push(
        '<div class="dd-cart-discount dd-discount-chip" data-dd-discount-chip data-discount-code="' + escAttr(title) + '">' +
        DISCOUNT_ICON_SVG + '<span>' + escHtml(title) + '</span>' +
        '<button type="button" class="dd-cart-discount__remove" aria-label="Remove discount ' + escAttr(title) +
        '"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></button></div>'
      );
    }
    return out.join('');
  }

  function updateDrawerFooterMeta(drawer, cart) {
    var f_ = facade();
    var formatMoney = f_.formatMoney || function (c) { var f = (c / 100).toFixed(2); return '$' + (f.slice(-3) === '.00' ? f.slice(0, -3) : f); };

    var summaryEl = drawer.querySelector('[data-dd-total-discount]');
    if (summaryEl) summaryEl.setAttribute('data-dd-total-discount', String(cartSavingsCents(cart) || 0));

    var discHost = drawer.querySelector('[data-dd-cart-discounts]');
    if (discHost) {
      withLocalShimmer(discHost, function () { discHost.innerHTML = renderCartLevelDiscountsHtml(cart); });
    }

    if (window.ddDiscountChips && typeof window.ddDiscountChips.render === 'function') window.ddDiscountChips.render();

    var savingsEl = drawer.querySelector('[data-dd-savings]');
    if (savingsEl) {
      var savingsMoney = savingsEl.querySelector('[data-dd-savings-money]');
      var isCustomer = document.documentElement.getAttribute('data-glass-customer') === 'true';
      var cents = cartSavingsCents(cart);
      withLocalShimmer(savingsEl, function () {
        if (cents > 0 && isCustomer) {
          savingsEl.style.display = '';
          if (savingsMoney) savingsMoney.textContent = formatMoney(cents);
        } else {
          savingsEl.style.display = 'none';
          if (savingsMoney) savingsMoney.textContent = '';
        }
      });
    }
  }

  function tryApplyGranularMiniCart(cart) {
    var f_ = facade();
    var isProtectionLine = f_.isProtectionLine || function () { return false; };

    if (!cart || cart.errors || !Array.isArray(cart.items)) return false;
    var drawer = document.querySelector('cart-drawer[data-dd-cart-shell="mini"]') || document.querySelector('cart-drawer');
    if (!drawer || !drawer.querySelector('[data-dd-cart-items]')) return false;
    var contents = drawer.querySelector('#CartDrawer-CartItems.drawer__contents') || drawer.querySelector('.drawer__contents.js-contents');
    if (!contents) return false;

    var itemByKey = {};
    var lineNumberByKey = {};
    var merchKeys = {};
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      itemByKey[it.key] = it;
      lineNumberByKey[it.key] = i + 1;
      if (!isProtectionLine(it)) merchKeys[it.key] = true;
    }

    var domKeys = {};
    var rows = contents.querySelectorAll('[data-dd-cart-item]');
    for (var r = 0; r < rows.length; r++) {
      var k = rows[r].dataset.lineKey;
      if (k) domKeys[k] = true;
    }

    for (var mk in merchKeys) { if (!domKeys[mk]) return false; }

    var groups = contents.querySelectorAll('.dd-item-group');
    for (var g = 0; g < groups.length; g++) {
      var grp = groups[g];
      var ci = grp.querySelector('[data-dd-cart-item]');
      if (!ci || !ci.dataset.lineKey) continue;
      if (!merchKeys[ci.dataset.lineKey]) grp.remove();
    }

    rows = contents.querySelectorAll('[data-dd-cart-item]');
    for (var r2 = 0; r2 < rows.length; r2++) {
      var row = rows[r2];
      var key = row.dataset.lineKey;
      var line = itemByKey[key];
      if (!line) return false;
      var lineNumber = lineNumberByKey[key] || 0;
      if (lineNumber > 0) row.setAttribute('data-line', String(lineNumber));

      var keyedNodes = row.querySelectorAll('[data-line-key]');
      for (var kn = 0; kn < keyedNodes.length; kn++) keyedNodes[kn].setAttribute('data-line-key', key);
      var linedNodes = row.querySelectorAll('[data-line]');
      for (var ln = 0; ln < linedNodes.length; ln++) { if (lineNumber > 0) linedNodes[ln].setAttribute('data-line', String(lineNumber)); }

      var input = row.querySelector('[data-dd-qty-input]');
      if (input) { input.value = String(line.quantity); input.setAttribute('value', String(line.quantity)); }

      var priceRow = row.querySelector('.dd-cart-item__price-row');
      if (priceRow && !priceRow.querySelector('.dd-cart-item__login-price')) {
        withLocalShimmer(priceRow, function () { priceRow.innerHTML = renderLinePriceRowInner(line); });
      }
    }
    return true;
  }

  function replaceCartSections(sections, opts) {
    if (!sections) return;
    opts = opts || {};
    _s.granularFooterPending = !!opts.skipDrawer;

    var snap = captureCartScrollSnapshot();
    var isCartPage = /\/cart\b/.test(window.location.pathname);

    try {
      if (isCartPage) {
        var el1 = document.getElementById('main-cart-items');
        var el2 = document.getElementById('main-cart-footer');
        var didSwapCartPage = false;
        if (el1 && el1.dataset.id && sections[el1.dataset.id]) {
          var target1 = el1.querySelector('.js-contents');
          if (target1) { var html1 = parseSectionHTML(sections[el1.dataset.id], '.js-contents'); if (html1) { target1.innerHTML = html1; didSwapCartPage = true; } }
        }
        if (el2 && el2.dataset.id && sections[el2.dataset.id]) {
          var target2 = el2.querySelector('.js-contents');
          if (target2) {
            var html2 = parseSectionHTML(sections[el2.dataset.id], '.js-contents');
            if (html2) {
              /* The cart-page footer goes through a wholesale `innerHTML`
                 swap (drawer uses granular `patchDrawerFromSection`, but
                 the page-side equivalent doesn't exist yet). That swap
                 nukes ephemeral DOM state the shopper just produced —
                 most visibly the discount/note accordion `is--open`
                 class, the inline `.dd-discount__feedback` success
                 message, the inline cart toast (`[data-dd-cart-toast]`),
                 and any in-progress input the user typed but didn't yet
                 submit. Capture-and-restore around the swap so post-
                 mutation feedback survives the section replace until
                 a proper granular patcher is built. */
              var preserved = captureCartPageFooterEphemera(target2);
              target2.innerHTML = html2;
              restoreCartPageFooterEphemera(target2, preserved);
              didSwapCartPage = true;
            }
          }
        }
        if (didSwapCartPage) emitItemsSwapped('cart-page');
      }

      if (!opts.skipDrawer && sections['cart-drawer']) {
        if (_sectionReplaceInProgress) {
          _sectionReplaceQueue.push(sections['cart-drawer']);
          debugLog('section-replace:queued', { queueLength: _sectionReplaceQueue.length });
        } else {
          var didPatch = patchDrawerFromSection(sections['cart-drawer']);
          if (!didPatch) {
            var m = metrics();
            m.drawerPatchFail = (m.drawerPatchFail || 0) + 1;
            console.error('[cart-paint] patchDrawerFromSection failed in replaceCartSections — patch is the only in-drawer path; investigate DOM mismatch');
          }
        }
      }

      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble && sections['cart-icon-bubble']) {
        var bubbleHTML = parseSectionHTML(sections['cart-icon-bubble'], '.shopify-section');
        if (bubbleHTML) bubble.innerHTML = bubbleHTML;
      }
    } finally {
      if (opts && opts.scrollToTop) {
        requestAnimationFrame(function () {
          var drawerList = document.querySelector('[data-dd-cart-items]');
          if (drawerList) drawerList.scrollTop = 0;
          var main = document.getElementById('main-cart-items');
          var mainContents = main ? main.querySelector('.js-contents') : null;
          if (mainContents) mainContents.scrollTop = 0;
        });
      } else {
        restoreCartScrollSnapshot(snap);
      }
    }
  }

  window._ddCartPaint = {
    withLocalShimmer: withLocalShimmer,
    rehydrateBogosVisibility: rehydrateBogosVisibility,
    replaceDrawerInnerHTML: replaceDrawerInnerHTML,
    replaceCartDrawerRootHTML: replaceCartDrawerRootHTML,
    parseCartDrawerRootInnerHtml: parseCartDrawerRootInnerHtml,
    patchDrawerFromSection: patchDrawerFromSection,
    applyFooterFromCart: applyFooterFromCart,
    consumeGranularFooterPending: consumeGranularFooterPending,
    normalizeSectionList: normalizeSectionList,
    fetchSectionsFallback: fetchSectionsFallback,
    fetchSectionsBySectionId: fetchSectionsBySectionId,
    hasAllRequestedSections: hasAllRequestedSections,
    parseSectionHTML: parseSectionHTML,
    captureCartScrollSnapshot: captureCartScrollSnapshot,
    restoreCartScrollSnapshot: restoreCartScrollSnapshot,
    replaceCartSections: replaceCartSections,
    tryApplyGranularMiniCart: tryApplyGranularMiniCart,
    updateDrawerFooterMeta: updateDrawerFooterMeta,
    renderCodeChipsHtml: renderCodeChipsHtml,
    renderCartLevelDiscountsHtml: renderCartLevelDiscountsHtml,
    /* Freshness window: 60s. The previous 2s value was tuned for the
       "user mutated the cart, painter ran, drawer needs to open
       quickly" case — but it expired so fast that any casual reopen
       (close drawer, browse, reopen 5s later) hit `cart-drawer.js
       openFromCartIcon` cold path, which fetches /cart?sections=…
       and re-runs the painter on byte-identical SSR. With the
       innerHTML equality gates in `patchDrawerFromSection` above the
       repaint is now a true no-op when nothing changed, so the
       freshness window's only remaining job is to skip the *fetch*
       itself. 60s is long enough to cover normal reopen cadence and
       short enough that cross-tab cart changes (rare) catch up
       quickly via the next real user action that calls cartMutate. */
    isSectionFooterFresh: function () { return (Date.now() - _s.sectionFooterFreshTs) < 60000; }
  };
})();
